<template>
  <div
    v-if="shouldShow"
    class="step-data-display"
    :class="{
      'step-data-display--loading':
        isStep('contacts') && importStatus && importStatus.pending,
    }"
    :style="
      isStep('contacts') && importStatus && importStatus.pending
        ? { '--progress': importProgress + '%' }
        : {}
    "
  >
    <!-- Files Step -->
    <div v-if="isStep('files')" class="step-data-display__content">
      <div
        v-if="filesCount > 0"
        class="step-data-display__state step-data-display__state--success"
      >
        <span class="step-data-display__text"
          >{{ filesCount }} {{ pluralize('file', filesCount) }} imported</span
        >
      </div>
      <div
        v-else-if="isStepCompleted"
        class="step-data-display__state step-data-display__state--completed"
      >
        <span class="step-data-display__text">{{
          step.summaryText || 'Files imported'
        }}</span>
      </div>
      <div
        v-else
        class="step-data-display__state step-data-display__state--empty"
      >
        <span class="step-data-display__text">No files imported yet</span>
      </div>
    </div>

    <!-- Contacts Import Step -->
    <div v-else-if="isStep('contacts')" class="step-data-display__content">
      <template v-if="importStatus">
        <div
          v-if="importStatus.pending"
          class="step-data-display__state step-data-display__state--loading"
        >
          <v-progress-circular
            indeterminate
            size="16"
            width="2"
            color="primary"
            class="step-data-display__spinner"
          />
          <span class="step-data-display__text">Importing...</span>
          <span class="step-data-display__progress-text"
            >{{ importStatus.processed }}/{{ importStatus.total }} ({{
              importProgress
            }}%)</span
          >
        </div>

        <div
          v-else-if="importStatus.imported > 0"
          class="step-data-display__state step-data-display__state--success"
        >
          <span class="step-data-display__text">
            {{ importStatus.imported }}
            {{ pluralize('contact', importStatus.imported) }} imported
          </span>
        </div>
      </template>
      <div
        v-else-if="isStepCompleted"
        class="step-data-display__state step-data-display__state--completed"
      >
        <span class="step-data-display__text">{{
          step.summaryText || 'Contacts imported'
        }}</span>
      </div>
      <div
        v-else
        class="step-data-display__state step-data-display__state--empty"
      >
        <span class="step-data-display__text">No contacts imported yet</span>
      </div>
    </div>

    <!-- Invite Contacts Step -->
    <div
      v-else-if="isStep('invite-contacts')"
      class="step-data-display__content"
    >
      <div
        v-if="invitedCount > 0"
        class="step-data-display__state step-data-display__state--success"
      >
        <span class="step-data-display__text"
          >{{ invitedCount }}
          {{ pluralize('contact', invitedCount) }} invited</span
        >
      </div>
      <div
        v-else-if="isStepCompleted"
        class="step-data-display__state step-data-display__state--completed"
      >
        <span class="step-data-display__text">{{
          step.summaryText || 'Completed'
        }}</span>
      </div>
      <div
        v-else
        class="step-data-display__state step-data-display__state--empty"
      >
        <span class="step-data-display__text">No contacts invited yet</span>
      </div>
    </div>

    <!-- SMS Step -->
    <div v-else-if="isStep('sms')" class="step-data-display__content">
      <div
        v-if="isStepCompleted"
        class="step-data-display__state step-data-display__state--completed"
      >
        <span class="step-data-display__text">{{
          step.summaryText || 'SMS-Line configured'
        }}</span>
      </div>
      <div
        v-else
        class="step-data-display__state step-data-display__state--empty"
      >
        <span class="step-data-display__text">SMS-Line not configured yet</span>
      </div>
    </div>

    <!-- Finish Step -->
    <div v-else-if="isStep('finish')" class="step-data-display__content">
      <div
        v-if="isStepCompleted"
        class="step-data-display__state step-data-display__state--completed"
      >
        <span class="step-data-display__text">{{
          step.summaryText || 'Setup completed!'
        }}</span>
      </div>
      <div
        v-else
        class="step-data-display__state step-data-display__state--empty"
      >
        <span class="step-data-display__text"
          >Complete all previous steps first</span
        >
      </div>
    </div>

    <!-- Generic completed step fallback -->
    <div v-else-if="isStepCompleted" class="step-data-display__content">
      <div class="step-data-display__state step-data-display__state--completed">
        <span class="step-data-display__text">{{
          step.summaryText || 'Completed'
        }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'StepDataDisplay',
  props: {
    step: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState('files', { files: 'all' }),
    ...mapGetters(['contactsImportStatus', 'invitedContacts']),
    ...mapGetters('users', ['hasAchievement']),

    isStepCompleted() {
      return (
        this.step?.completed ||
        (this.step?.event && this.hasAchievement(this.step.event))
      );
    },

    shouldShow() {
      return Boolean(this.step);
    },

    stepId() {
      return this.step?.id || '';
    },

    // Files step data
    filesCount() {
      return Array.isArray(this.files) ? this.files.length : 0;
    },

    importStatus() {
      const { stats } = this.contactsImportStatus || {};

      if (!stats) return null;

      const total = Object.values(stats).reduce(
        (sum, val) => sum + (val || 0),
        0
      );
      const pending = stats.pending || 0;

      return {
        total,
        pending,
        processed: total - pending,
        imported: stats.sent || 0,
      };
    },

    importProgress() {
      if (!this.importStatus) return 0;

      const { total, processed } = this.importStatus;

      if (total === 0) return 0;
      const progress = Math.round((processed / total) * 100);
      return Math.max(1, progress); // Ensure at least 1% so bar is visible
    },

    // Invite contacts data
    invitedCount() {
      return Array.isArray(this.invitedContacts)
        ? this.invitedContacts.length
        : 0;
    },
  },

  methods: {
    isStep(stepId) {
      return this.stepId === stepId;
    },

    pluralize(entity, count) {
      if (typeof entity !== 'string') return '';
      return `${entity}${count === 1 ? '' : 's'}`;
    },
  },
};
</script>

<style lang="less" scoped>
.step-data-display {
  margin: 6px 0;
  padding: 8px 12px;
  background: #f5f5f5;
  border-left: 3px solid var(--v-success-base);
  border-radius: 4px;
  font-size: 0.85rem;
  color: #333;
  position: relative;

  &--loading {
    background: linear-gradient(
      to right,
      #e3f2fd 0%,
      #e3f2fd var(--progress, 0%),
      #f5f5f5 var(--progress, 0%),
      #f5f5f5 100%
    );
    border-left-color: #2196f3;
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__state {
    display: flex;
    align-items: center;
    gap: 8px;

    &--success {
      color: var(--v-success-base);
    }

    &--loading {
      color: #1976d2;
      font-weight: 600;
    }

    &--empty {
      color: #757575;
      font-style: italic;
    }

    &--completed {
      color: var(--v-success-base);
      font-weight: 600;
    }
  }

  &__text {
    font-weight: 500;
    font-size: 14px;
  }

  &__progress-text {
    font-size: 0.75rem;
    color: #666;
    margin-left: 6px;
    font-weight: 500;
  }

  &__spinner {
    margin-right: 4px;
  }
}
</style>
